import { useForm, FormProvider } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import styles from './styles.module.scss'
import { SubmitButton } from '@components/ui/Button'
import ButtonMinimal from '@components/ui/Button/ButtonMinimal'
import { AddressFormWrapper } from './AddressFormWrapper'
import { AddressData } from '../config'
import { useAuth } from '@api/accounts/auth/hooks/useAuth'
import { ErrorField } from './shared/InputField/InputErrorMessage'
import { SubmitButtonState } from '@components/ui/Button/SubmitButton'
import { addressUpdate } from '../api/addressUpdate'
import { addressDelete } from '../api/addressDelete'
import { getErrorMessage } from '@api/graphqlShopify'
import {
  AddressFormFields,
  UpdateAddressData,
  UpdateAddressSchema
} from './shared/AddressFormFields'
import FormButtonGroup from '@components/ui/formElements/form/shared/FormButtonGroup'

export type DrawerStateProps = AddressData & {
  defaultAddress: boolean
  territoryCode: string
}

export function UpdateAddress({
  drawerState,
  handleCloseAccountDetailsDrawer
}: {
  drawerState: {
    data: DrawerStateProps
  }
  handleCloseAccountDetailsDrawer: () => void
}) {
  const { refreshUserProfile, makeRequestWithRefreshCheck } = useAuth()
  const [isProvincesLoading, setIsProvincesLoading] = useState(false)
  const [buttonState, setButtonState] = useState<SubmitButtonState>('disabled')
  const methods = useForm<UpdateAddressData & { customError?: string }>({
    mode: 'onChange',
    resolver: zodResolver(UpdateAddressSchema),
    defaultValues: {
      ...drawerState.data
    }
  })

  const formValues = methods.getValues()
  const formErrors = methods.formState.errors

  const { formState } = methods
  const { isValid, isDirty } = formState

  useEffect(() => {
    methods.register('territoryCode', { required: true })
  }, [methods, formValues.territoryCode])

  useEffect(() => {
    if (!isProvincesLoading && isValid && isDirty) {
      setButtonState('ready')
    } else {
      setButtonState('disabled')
    }
  }, [isValid, isDirty, isProvincesLoading])

  if (!drawerState.data) {
    handleCloseAccountDetailsDrawer()
    return null
  }

  const handleUpdateAddress = async (data: DrawerStateProps) => {
    setButtonState('loading')
    try {
      await makeRequestWithRefreshCheck(addressUpdate, data)
      await refreshUserProfile()

      setTimeout(() => {
        setButtonState('ready')
        handleCloseAccountDetailsDrawer()
      }, 2000)
    } catch (e) {
      const error = e as unknown as Error
      const errorMessage =
        getErrorMessage(error) || 'Something went wrong. Try again later.'

      methods.setError(
        'customError',
        {
          type: 'focus',
          message: errorMessage
        },
        {
          shouldFocus: true
        }
      )
      setButtonState('ready')
    }
  }

  const handleDeleteAddress = async (id: string) => {
    try {
      await makeRequestWithRefreshCheck(addressDelete, id)
      await refreshUserProfile()
      handleCloseAccountDetailsDrawer()
    } catch (e) {
      const error = e as unknown as Error
      const errorMessage =
        getErrorMessage(error) || 'Something went wrong. Try again later.'
      methods.setError(
        'customError',
        {
          type: 'focus',
          message: errorMessage
        },
        {
          shouldFocus: true
        }
      )
    }
  }

  return (
    <FormProvider {...methods}>
      <AddressFormWrapper title="Edit Address">
        <form onSubmit={methods.handleSubmit(handleUpdateAddress)}>
          <input
            type="hidden"
            {...methods.register('id')}
            name="id"
            value={drawerState.data.id}
          />
          <AddressFormFields
            setIsProvincesLoading={setIsProvincesLoading}
            formValues={formValues}
            formSetFieldValue={methods.setValue}
          />
          <div
            className={`${styles.AccountForm__Buttons} ${
              formErrors.customError?.message
                ? styles.AccountForm__Buttons__withError
                : ''
            }`}
          >
            {formErrors.customError?.message && (
              <ErrorField message={formErrors.customError.message} />
            )}
            <FormButtonGroup
              variant="primary-inline"
              primaryCta={
                <SubmitButton
                  state={buttonState}
                  disabled={buttonState === 'disabled'}
                  type="submit"
                  fullWidth="never"
                >
                  Save
                </SubmitButton>
              }
              secondaryCta={
                <ButtonMinimal
                  underlined={true}
                  onClick={() =>
                    drawerState.data.id &&
                    handleDeleteAddress(drawerState.data.id)
                  }
                >
                  Delete Address
                </ButtonMinimal>
              }
            />
          </div>
        </form>
      </AddressFormWrapper>
    </FormProvider>
  )
}
